<!-- packages/studio/src/components/army/UnitWeaponsForm.vue -->
<template>
  <EditableList
    :items="modelValue"
    add-label="Add weapon"
    remove-label="Remove weapon"
    @add="add"
    @remove="remove"
  >
    <template #row="{ index }">
      <div class="weapon-form">
        <StIdField
          :model-value="modelValue[index].id"
          prefix="weapon"
          @update:model-value="patch(index, { id: $event })"
        />
        <div class="weapon-head">
          <StField label="Name"
            ><StTextInput
              :model-value="modelValue[index].name"
              @update:model-value="patch(index, { name: $event ?? '' })"
          /></StField>
          <StField label="Type">
            <StSelect
              :model-value="modelValue[index].type"
              @update:model-value="setType(index, Number($event))"
            >
              <option :value="WeaponType.Melee">Melee</option>
              <option :value="WeaponType.Ranged">Ranged</option>
            </StSelect>
          </StField>
        </div>

        <div class="weapon-stats">
          <!-- Range only applies to ranged weapons; melee weapons have none. -->
          <StField v-if="isRanged(modelValue[index])" label="Range"
            ><StTextInput
              :model-value="modelValue[index].range ?? ''"
              @update:model-value="patch(index, { range: $event || null })"
          /></StField>
          <StField label="Attacks"
            ><StTextInput
              :model-value="modelValue[index].attack"
              @update:model-value="patch(index, { attack: $event ?? '' })"
          /></StField>
          <StField label="Hit"
            ><StTextInput
              :model-value="modelValue[index].hit"
              @update:model-value="patch(index, { hit: $event ?? '' })"
          /></StField>
          <StField label="Wound"
            ><StTextInput
              :model-value="modelValue[index].wound"
              @update:model-value="patch(index, { wound: $event ?? '' })"
          /></StField>
          <StField label="Rend"
            ><StTextInput
              :model-value="modelValue[index].rend"
              @update:model-value="patch(index, { rend: $event ?? '' })"
          /></StField>
          <StField label="Damage"
            ><StTextInput
              :model-value="modelValue[index].damage"
              @update:model-value="patch(index, { damage: $event ?? '' })"
          /></StField>
        </div>

        <StField group label="Abilities">
          <KeywordsInput
            raw
            noun="Ability"
            suggestion-scope="weapon"
            placeholder="e.g. Crit (2 Hits)"
            :model-value="modelValue[index].abilities"
            @update:model-value="patch(index, { abilities: $event })"
          />
        </StField>
      </div>
    </template>
  </EditableList>
</template>

<script setup lang="ts">
import type { IWeapon } from '@scrolldex/shared';
import { WeaponType, makeWeapon } from '@scrolldex/shared';
import EditableList from '../EditableList.vue';
import KeywordsInput from '../KeywordsInput.vue';
import { StField, StTextInput, StSelect, StIdField } from '../ui/index.js';

const props = defineProps<{ modelValue: IWeapon[] }>();
const emit = defineEmits<{ (e: 'update:modelValue', value: IWeapon[]): void }>();

function isRanged(w: IWeapon): boolean {
  return w.type === WeaponType.Ranged;
}
function patch(index: number, part: Partial<IWeapon>) {
  emit(
    'update:modelValue',
    props.modelValue.map((w, i) => (i === index ? { ...w, ...part } : w)),
  );
}
// Switching to melee clears any range value so it doesn't linger hidden.
function setType(index: number, type: number) {
  const part: Partial<IWeapon> = { type };
  if (type === WeaponType.Melee) part.range = null;
  patch(index, part);
}
function add() {
  emit('update:modelValue', [...props.modelValue, makeWeapon('New weapon')]);
}
function remove(index: number) {
  emit(
    'update:modelValue',
    props.modelValue.filter((_, i) => i !== index),
  );
}
</script>

<style scoped>
.weapon-form {
  display: flex;
  flex-direction: column;
  gap: var(--st-space-2);
}
.weapon-head {
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: var(--st-space-2);
  align-items: end;
}
.weapon-stats {
  display: flex;
  flex-wrap: wrap;
  gap: var(--st-space-2);
}
.weapon-stats :deep(.st-field) {
  flex: 1 1 5em;
  min-width: 5em;
}
</style>
